import type { ADFEntity } from '@atlaskit/adf-utils/types';

// Maximum number of JSON.parse iterations to apply to a stringified document.
// In practice the wire shape is at most double-encoded, so 3 iterations gives
// us headroom while still bounding the loop.
const MAX_DOCUMENT_DECODE_PASSES = 3;

export interface CollabDraftResultLike {
	data?: {
		collabDraft?: {
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			document?: unknown;
		} | null;
	} | null;
}

/**
 * Decodes a collab-draft document value into a plain ADF object.
 *
 * Handles all three on-the-wire shapes observed in practice for
 * `result.data.collabDraft.document`:
 *
 *   1. An already-parsed object — returned as-is.
 *   2. A single-encoded JSON string — `JSON.parse` once → object.
 *   3. A double-encoded JSON string (the wire form returned by some
 *      Apollo/preload paths) — `JSON.parse` yields another JSON string,
 *      which must be parsed again to recover the object.
 *
 * Returns `null` when `document` is `null`/`undefined`, when any intermediate
 * `JSON.parse` call throws, or when the value never resolves to an object
 * within `MAX_DOCUMENT_DECODE_PASSES` iterations (e.g. a JSON-encoded
 * primitive such as `"42"`).
 */
export const decodeCollabDraftDocument = (document: unknown): ADFEntity | null => {
	if (document == null) {
		return null;
	}

	let candidate: unknown = document;

	for (let pass = 0; pass < MAX_DOCUMENT_DECODE_PASSES; pass++) {
		if (typeof candidate !== 'string') {
			break;
		}
		try {
			candidate = JSON.parse(candidate);
		} catch {
			return null;
		}
	}

	// Still a string after exhausting all decode passes, or resolved to a
	// non-object primitive (number, boolean, null, etc.) — give up.
	if (typeof candidate !== 'object' || candidate === null) {
		return null;
	}

	return candidate as ADFEntity;
};

const parsedAdfByCollabDraftResult = new WeakMap<object, ADFEntity | null>();

/**
 * Extracts and decodes the ADF document from a resolved
 * `preloadCollabDraftContent` result, decoding it at most once per result
 * object so edit/live consumers can share a single parse. Returns `null` when
 * there is no decodable `data.collabDraft.document`.
 */
export const parseCollabDraftAdf = (
	collabDraftResult: CollabDraftResultLike | null | undefined,
): ADFEntity | null => {
	if (!collabDraftResult || typeof collabDraftResult !== 'object') {
		return null;
	}

	if (parsedAdfByCollabDraftResult.has(collabDraftResult)) {
		return parsedAdfByCollabDraftResult.get(collabDraftResult) ?? null;
	}

	const parsedAdf = decodeCollabDraftDocument(collabDraftResult.data?.collabDraft?.document);
	parsedAdfByCollabDraftResult.set(collabDraftResult, parsedAdf);
	return parsedAdf;
};
